import { useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { FRAME_COUNT } from './data/loadFrame';
import { useFrameLoader } from './data/useFrameLoader';
import { PointCloud } from './scene/PointCloud';
import { Cuboids, type HoverInfo } from './scene/Cuboids';
import { KeyboardControls } from './scene/KeyboardControls';
import { Hud, Overlay } from './ui/Hud';
import { CuboidTooltip } from './ui/CuboidTooltip';
import { Timeline } from './ui/Timeline';
import { CachePanel } from './ui/CachePanel';
import { ControlsHint } from './ui/ControlsHint';
import { SignatureCard } from './ui/SignatureCard';

/**
 * Steps 2–5: render the current frame's point cloud (height-colored) and
 * cuboids (semi-transparent + edges, hover for details), step through all 50
 * frames via the timeline, and serve frames from a memory-bounded FrameCache.
 *
 * The world is Z-up (LiDAR convention), so the ground is the XY plane — which
 * also lines up with the WASD (ground-plane) / QE (vertical) controls to come.
 */
export function App() {
  const [index, setIndex] = useState(0);
  const { data, loading, error } = useFrameLoader(index);
  const [hover, setHover] = useState<HoverInfo | null>(null);

  // Switching frames invalidates any hovered cuboid; clear the tooltip.
  const goToFrame = (next: number) => {
    setHover(null);
    setIndex(next);
  };

  const frame = data?.frame ?? null;

  return (
    <>
      <Canvas
        camera={{ position: [0, -60, 40], fov: 55, near: 0.1, far: 3000 }}
        onCreated={({ camera }) => {
          camera.up.set(0, 0, 1); // Z-up world
          camera.lookAt(0, 0, 0);
        }}
      >
        <color attach="background" args={['#0b0e14']} />
        {data && (
          <PointCloud key={`pc-${data.frame.id}`} positions={data.frame.positions} colors={data.colors} />
        )}
        {frame && (
          <Cuboids cuboids={frame.cuboids} hoveredId={hover?.cuboid.uuid ?? null} onHover={setHover} />
        )}
        <OrbitControls makeDefault />
        <KeyboardControls />
      </Canvas>

      {!frame && !error && <Overlay>Loading frame {index}…</Overlay>}
      {error && <Overlay>⚠ {error}</Overlay>}
      {frame && <Hud frame={frame} />}
      {hover && <CuboidTooltip info={hover} />}
      <CachePanel current={index} count={FRAME_COUNT} />
      <ControlsHint />
      <SignatureCard />
      <Timeline index={index} count={FRAME_COUNT} loading={loading} onChange={goToFrame} />
    </>
  );
}
